'use client';

import React, { createContext, useContext, useState, useEffect } from 'react';
import { useRouter, usePathname } from 'next/navigation';

interface User {
  id: number;
  email: string;
  name: string;
  role: 'admin' | 'partner' | 'user';
  company_name?: string;
  phone?: string;
  partner_id?: string;
  nbc_user_id?: number;
}

interface AuthContextType {
  user: User | null;
  token: string | null;
  loading: boolean;
  login: (token: string, user: User) => void;
  logout: () => void;
  isAuthenticated: boolean;
  hasRole: (role: 'admin' | 'partner' | 'user') => boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const router = useRouter();
  const pathname = usePathname();

  useEffect(() => {
    // Cargar datos de autenticación desde localStorage
    const storedToken = localStorage.getItem('auth_token');
    const storedUser = localStorage.getItem('auth_user');

    if (storedToken && storedUser) {
      try {
        const parsedUser = JSON.parse(storedUser);
        setToken(storedToken);
        setUser(parsedUser);
        
        // Verificar token con el servidor
        verifyToken(storedToken);
      } catch (error) {
        console.error('Error parsing stored user:', error);
        logout();
      }
    }
    
    setLoading(false);
  }, []);

  const verifyToken = async (tokenToVerify: string) => {
    try {
      const response = await fetch('/api/auth/verify', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ token: tokenToVerify }),
      });

      const data = await response.json();

      if (!data.valid) {
        console.log('Token expired or invalid, logging out');
        logout();
      }
    } catch (error) {
      console.error('Error verifying token:', error);
    }
  };

  const login = (newToken: string, newUser: User) => {
    setToken(newToken);
    setUser(newUser);
    localStorage.setItem('auth_token', newToken);
    localStorage.setItem('auth_user', JSON.stringify(newUser));
  };

  const logout = () => {
    // INMEDIATAMENTE limpiar localStorage (primero que nada)
    localStorage.removeItem('auth_token');
    localStorage.removeItem('auth_user');
    
    // Resetear estado ANTES de redirigir
    setToken(null);
    setUser(null);
    setLoading(false); // Asegurar que loading esté en false
    
    // Determinar la ruta de login según la ubicación actual
    const currentPath = window.location.pathname;
    let loginPath = '/admin/login';
    
    if (currentPath.startsWith('/partner')) {
      loginPath = '/partner/login';
    } else if (currentPath.startsWith('/admin')) {
      loginPath = '/admin/login';
    }
    
    // Usar replace en lugar de href para evitar que quede en historial
    // Y usar setTimeout para asegurar que el estado se limpie primero
    setTimeout(() => {
      window.location.replace(loginPath);
    }, 0);
  };

  const hasRole = (role: 'admin' | 'partner' | 'user') => {
    if (!user) return false;
    // Admin tiene acceso a todo
    if (user.role === 'admin') return true;
    return user.role === role;
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        token,
        loading,
        login,
        logout,
        isAuthenticated: !!user && !!token,
        hasRole,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

// Hook para proteger rutas
export function useRequireAuth(allowedRoles?: ('admin' | 'partner' | 'user')[]) {
  const { user, loading, isAuthenticated } = useAuth();
  const router = useRouter();
  const pathname = usePathname();

  useEffect(() => {
    if (!loading) {
      // Si no está autenticado, redirigir a login
      if (!isAuthenticated) {
        if (!pathname?.includes('/login') && !pathname?.includes('/register')) {
          router.push('/admin/login');
        }
        return;
      }

      // Si hay roles específicos requeridos, verificar
      if (allowedRoles && user) {
        const hasPermission = allowedRoles.includes(user.role) || user.role === 'admin';
        if (!hasPermission) {
          router.push('/admin'); // Redirigir a dashboard si no tiene permisos
        }
      }
    }
  }, [loading, isAuthenticated, user, allowedRoles, router, pathname]);

  return { user, loading, isAuthenticated };
}
